import {
  call, takeLatest, put, select,
} from 'redux-saga/effects';
import { api } from '@/utils/api';
import gatorProtectActions from './gatorProtect.actions';
import { GatorProtectActionTypes } from './gatorProtect.types';
import { config } from '@/config';
import { isBRServer } from '@/utils/Validators/validation';
import logger from '@/utils/logger';
import { fastCheckoutActions } from '../fastcheckout';
import { CREDIT_CARD, CREDIT_CARD_ES } from '@/config/billing/paymentMethods';

const { WHMCS_URL, API_URL } = config;

export function extractGatorProtectInvoiceId(response) {
  const invoicePayload = response?.data?.data;
  if (invoicePayload == null) {
    return undefined;
  }
  return invoicePayload.invoice_id ?? invoicePayload.invoiceId;
}

function* listPlans({ payload }) {
  const { headerId } = payload;
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v3/gator-protect${headerId ? `/${headerId}` : ''}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
  };

  yield put(gatorProtectActions.loading(true));

  try {
    const response = yield call(api.whmcs.get, request);
    const plans = response && response.data && response.data.data ? response.data.data : [];
    yield put(gatorProtectActions.plans.set(plans));
    yield put(gatorProtectActions.plans.success());
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas gatorProtect - method listPlans', error);
    yield put(gatorProtectActions.plans.failure());
  } finally {
    yield put(gatorProtectActions.loading(false));
  }
}

// /api/v3/gator-protect/4487835?start_date=2026-02-23
function* getPlanDetails({ payload: { hostingId, startDate } }) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v3/gator-protect/${hostingId}?start_date=${startDate}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
  };

  yield put(gatorProtectActions.planDetails.loaded(false));

  yield put(gatorProtectActions.loading(true));

  try {
    const response = yield call(api.whmcs.get, request);
    const details = response && response.data && response.data.data ? response.data.data : {};

    yield put(gatorProtectActions.planDetails.set(details));
    yield put(gatorProtectActions.planDetails.loaded(true));
    yield put(gatorProtectActions.planDetails.success());
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas gatorProtect - method getPlanDetails', error);
    yield put(gatorProtectActions.planDetails.failure());
  } finally {
    yield put(gatorProtectActions.loading(false));
  }
}

function* updateAutoProtect({ payload: { hostingId, autoProtect } }) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v3/gator-protect/${hostingId}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
    data: { auto_protect: autoProtect },
  };

  try {
    const response = yield call(api.whmcs.patch, request);
    const data = response?.data?.data;
    if (data != null) {
      const currentPlanDetails = yield select(state => state.gatorProtect.planDetails);
      yield put(gatorProtectActions.planDetails.set({ ...currentPlanDetails, ...data }));
    }
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas gatorProtect - method updateAutoProtect', error);
    yield put(gatorProtectActions.planDetails.setAutoProtect(!autoProtect));
  }
}

function* acceptTerms({ payload: { hostingId, termsAccepted } }) {
  if (hostingId == null) {
    return;
  }

  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v3/gator-protect/accept`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
    data: {
      hosting_id: hostingId,
      term_accept: termsAccepted,
    },
  };

  try {
    yield call(api.whmcs.post, request);
    yield put(gatorProtectActions.acceptTerms.success());
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas gatorProtect - method acceptTerms', error);
    yield put(gatorProtectActions.acceptTerms.failure());
  }
}

function* purchaseFastCheckout({ payload }) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const {
    productId,
    domain,
    redirectToInvoice,
    onPurchaseSuccess,
    hostingId,
    billingCycle,
  } = payload;
  const { defaultPaymentMethod } = yield select(state => state.summary);
  const creditCard = yield select(state => state.creditCard);

  const hasCreditCard = !!creditCard?.cardType;
  const creditCardConst = isBRServer ? CREDIT_CARD : CREDIT_CARD_ES;
  const paymentMethod = hasCreditCard ? creditCardConst : defaultPaymentMethod;

  yield put(fastCheckoutActions.loading(true));
  try {
    const fastCheckoutRequest = {
      url: `${API_URL}/fastcheckout`,
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Service-Token': jwtToken,
      },
      data: {
        products: [
          {
            id: productId,
            type: 'product',
            domain,
            quantity: 1,
            cycle: billingCycle ?? 'monthly',
            configoptions: {},
            shared_hosting_id: hostingId,
          },
        ],
        payment_method: paymentMethod,
      },
    };
    const response = yield call(api.whmcs.post, fastCheckoutRequest);
    const invoiceId = extractGatorProtectInvoiceId(response);

    if (typeof onPurchaseSuccess === 'function') {
      onPurchaseSuccess();
    }
    if (redirectToInvoice && invoiceId != null) {
      redirectToInvoice(invoiceId);
    }
    yield put(fastCheckoutActions.loading(false));
  } catch (e) {
    logger.error('Error purchaseFastCheckout', e);
    yield put(fastCheckoutActions.loading(false));
  }
}

function* purchaseUpgradeOrder({ payload }) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const {
    hostingId,
    newProductId,
    newBillingCycle,
    redirectToInvoice,
  } = payload;

  yield put(fastCheckoutActions.loading(true));
  try {
    const upgradeRequest = {
      url: `${WHMCS_URL}/api/v2/upgrades/order`,
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Service-Token': jwtToken,
      },
      data: {
        hosting_id: hostingId,
        new_product_id: newProductId,
        new_billing_cycle: newBillingCycle,
      },
    };
    const response = yield call(api.whmcs.post, upgradeRequest);
    const invoiceId = extractGatorProtectInvoiceId(response);

    if (redirectToInvoice && invoiceId != null) {
      redirectToInvoice(invoiceId);
    }
    yield put(fastCheckoutActions.loading(false));
  } catch (e) {
    logger.error('Error purchaseUpgradeOrder', e);
    yield put(fastCheckoutActions.loading(false));
  }
}

function* putAcknowledgement({ payload }) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const { hostingId } = payload;
  const brand = isBRServer ? 'br' : 'es';

  const upgradeRequest = {
    url: `${WHMCS_URL}/api/v3/gator-protect/acked`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
    data: {
      shared_hosting_id: hostingId,
    },
  };

  try {
    yield call(api.whmcs.put, upgradeRequest);

    yield put(gatorProtectActions.acknowledgement.success());
  } catch (e) {
    logger.error('Error putAcknowledgement', e);
    yield put(gatorProtectActions.acknowledgement.failure());
  }
}

function* getThreatsData({ payload: { hostingId, perPage = 10, page = 1 } }) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v3/gator-protect/${hostingId}/files?page=${page}&per_page=${perPage}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
  };

  yield put(gatorProtectActions.planThreats.loading(true));

  try {
    const response = yield call(api.whmcs.get, request);
    const threats = response?.data?.data ?? {};

    threats.page = {
      currentPage: threats?.page?.current_page,
      perPage: threats?.page?.per_page,
      total: threats?.page?.total,
      lastPage: threats?.page?.last_page,
    };

    threats.files = (threats?.files ?? []).map(file => ({
      name: file.name,
      path: file.path,
      category: file.category,
      status: file.status,
      detectedAt: file.detected_at,
      remediatedAt: file.remediated_at,
    }));

    yield put(gatorProtectActions.planThreats.set(threats));
    yield put(gatorProtectActions.planThreats.success());
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas gatorProtect - method getThreatsData', error);
    yield put(gatorProtectActions.planThreats.failure());
  } finally {
    yield put(gatorProtectActions.planThreats.loading(false));
  }
}

function* sagas() {
  yield takeLatest(GatorProtectActionTypes.GATOR_PROTECT_LIST_PLANS_REQUEST, listPlans);
  yield takeLatest(GatorProtectActionTypes.GATOR_PROTECT_GET_PLAN_DETAILS_REQUEST, getPlanDetails);
  yield takeLatest(GatorProtectActionTypes.GATOR_PROTECT_UPDATE_AUTO_PROTECT_REQUEST, updateAutoProtect);
  yield takeLatest(GatorProtectActionTypes.GATOR_PROTECT_PURCHASE_FAST_CHECKOUT_REQUEST, purchaseFastCheckout);
  yield takeLatest(GatorProtectActionTypes.GATOR_PROTECT_PURCHASE_UPGRADE_ORDER_REQUEST, purchaseUpgradeOrder);
  yield takeLatest(GatorProtectActionTypes.GATOR_PROTECT_ACCEPT_TERMS_REQUEST, acceptTerms);
  yield takeLatest(GatorProtectActionTypes.GATOR_PROTECT_PUT_ACKNOWLEDGEMENT_REQUEST, putAcknowledgement);
  yield takeLatest(GatorProtectActionTypes.GATOR_PROTECT_PLAN_THREATS_REQUEST, getThreatsData);
}

export default sagas;
